import Link from "next/link";
import { v4 as uuidv4 } from "uuid";
import { useQueryClient } from "react-query";
import { ExportOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { DashboardV1State, DeepLinkType } from "pages/api/deep-links/types";
import { useCallback, useState } from "react";
import { Flex } from "src/components/Flex";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { useApplicationContext } from "src/hooks/useApplication";
import { SearchState } from "src/search/types";
import { DashboardSelect } from "../dashboards/DashboardSelect";
import {
  DEFAULT_COLUMN_COUNT,
  DEFAULT_ROW_HEIGHT,
} from "../dashboards/DashboardViewer";
import { ContentViews } from "./lib/useContentView";

export function BoardAddForm(props: {
  searchState: SearchState;
  viewValue: ContentViews;
  viewConfig: any;
  title: string;
  onClose(): void;
}) {
  const client = useQueryClient();
  const app = useApplicationContext();
  const [link, setLink] = useState(false);
  const [working, setWorking] = useState(false);
  const [boardId, setBoardId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const boards = useDeepLinks(DeepLinkType.BOARD);
  const board = boards.links.data?.find((link) => link.id === boardId);
  const canEditId = useCallback(
    ({ value: id }: { value: string }) => {
      const board = boards.links.data?.find((link) => link.id === id);
      if (!board) return false;
      const boardIsGlobal = !board?.orgId;
      const boardIsShared = !board?.userId;
      const userIsGlobal = app.role == "super" || app.user?.superuser;
      const userIsShared = app.role == "admin";
      if (boardIsGlobal) return Boolean(userIsGlobal);
      if (boardIsShared) return Boolean(userIsGlobal || userIsShared);
      return true;
    },
    [boards, app.role, app.user?.superuser]
  );
  return (
    <div>
      <DashboardSelect
        value={"Find a board..."}
        filter={canEditId}
        disabled={working}
        showResults
        onChange={setBoardId}
        unlock={() => {}}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        bordered
      />
      <div style={{ padding: 8, fontSize: 14 }}>
        <div style={{ margin: "8px 0" }}>
          {link ? "See the query you appended" : "Append this query to"}
        </div>
        {link ? (
          <Flex centered>
            <Button type="link">
              <Link
                href={{
                  pathname: "/[orgId]/t/new",
                  query: {
                    orgId: app.org_id,
                    url: `/${app.org_id}/boards/${boardId}`,
                  },
                }}
              >
                {board?.name || "no-name"} <ExportOutlined />
              </Link>
            </Button>
          </Flex>
        ) : (
          <Flex style={{ justifyContent: "flex-end", gap: 16 }}>
            <div>
              {board?.name ? (
                <span style={{ fontWeight: 600 }}>{board.name}</span>
              ) : (
                <em style={{ opacity: 0.5 }}>select board above</em>
              )}
            </div>
            <Button
              loading={working}
              type="primary"
              disabled={!board}
              onClick={() => {
                setWorking(true);
                const { data, layout, columnCount, rowHeight } = (board?.json ||
                  {}) as DashboardV1State;
                const i = uuidv4();
                data[i] = {
                  type: DeepLinkType.EXPLORE,
                  style: {},
                  title: props.title,
                  showTitle: true,
                  centerTitle: false,
                  transparent: false,
                  state: {
                    params: props.searchState,
                    viz: { value: props.viewValue, config: props.viewConfig },
                  },
                };
                const y = layout.reduce((a, c) => Math.max(a, c.y), 0);
                layout.push({
                  i,
                  x: 0,
                  y: y + 1,
                  w: columnCount || DEFAULT_COLUMN_COUNT,
                  h: Math.round(300 / (rowHeight || DEFAULT_ROW_HEIGHT)),
                });
                boards.update
                  .mutateAsync({
                    ...board!,
                    state: { ...board!.json, data, layout },
                  })
                  .then(() => {
                    client.invalidateQueries({
                      queryKey: [
                        "deep-links",
                        {
                          user_id: app.user?.id,
                          org_id: app.org_id,
                          type: DeepLinkType.EXPLORE,
                        },
                      ],
                    });
                    client.refetchQueries({
                      queryKey: [
                        "deep-links",
                        {
                          user_id: app.user?.id,
                          org_id: app.org_id,
                          type: DeepLinkType.EXPLORE,
                        },
                      ],
                    });

                    setLink(true);
                  })
                  .finally(() => {
                    setWorking(false);
                  });
              }}
            >
              Append
            </Button>
          </Flex>
        )}
      </div>
    </div>
  );
}
